import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ChatMessage,
  Conversation,
  MessageDeliveryStatus,
} from '../../message/types';
import { EmployeeConversationState } from '../types';
import {
  eeListConversations,
  eeGetConversation,
  eeCreateConversation,
  eeSendMessage,
  eeLoadMessages,
  eeMarkConversationRead,
} from '../actions';

const initialState: EmployeeConversationState = {
  conversations: [],
  totalDocs: 0,
  nextCursor: null,
  activeConversation: null,
  messages: [],
  messagesNextCursor: null,
  loading: false,
  messagesLoading: false,
  sendingMessage: false,
};

const employeeMessageSlice = createSlice({
  name: 'employeeMessage',
  initialState,
  reducers: {
    setActiveConversation: (
      state,
      action: PayloadAction<Conversation | null>,
    ) => {
      state.activeConversation = action.payload;
      state.messages = [];
      state.messagesNextCursor = null;
    },

    clearMessages: (state) => {
      state.messages = [];
      state.messagesNextCursor = null;
    },

    addOptimisticMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },

    updateMessageStatus: (
      state,
      action: PayloadAction<{
        tempId: string;
        status: MessageDeliveryStatus;
        serverMessage?: ChatMessage;
      }>,
    ) => {
      const { tempId, status, serverMessage } = action.payload;
      const idx = state.messages.findIndex((m) => m._tempId === tempId);
      if (idx !== -1) {
        if (serverMessage) {
          state.messages[idx] = {
            ...serverMessage,
            _status: status,
            _tempId: tempId,
          };
        } else {
          state.messages[idx]._status = status;
        }
      }
    },

    onNewMessage: (
      state,
      action: PayloadAction<{
        message: ChatMessage;
        unreadCount?: number;
        currentUserId?: string;
      }>,
    ) => {
      const { message, unreadCount, currentUserId } = action.payload;
      const isActiveConv =
        state.activeConversation?.id === message.conversationId;
      const isFromOther =
        String(message.senderUserId) !== String(currentUserId);

      // Update lastMessage on the conversation in the list
      const convIdx = state.conversations.findIndex(
        (c) => c.id === message.conversationId,
      );
      if (convIdx !== -1) {
        state.conversations[convIdx].lastMessage = message;
        state.conversations[convIdx].updatedAt = message.createdAt;

        if (!isActiveConv && isFromOther && unreadCount !== undefined) {
          state.conversations[convIdx].unreadCount = unreadCount;
        }

        // Move to top of list
        const [conv] = state.conversations.splice(convIdx, 1);
        state.conversations.unshift(conv);
      }

      // If this message belongs to the active conversation, add/replace
      if (
        state.activeConversation &&
        message.conversationId === state.activeConversation.id
      ) {
        let matchIdx = -1;
        if (message.uuid) {
          matchIdx = state.messages.findIndex(
            (m) => m._tempId === message.uuid || m.uuid === message.uuid,
          );
        }

        if (matchIdx === -1 && message.id) {
          matchIdx = state.messages.findIndex((m) => m.id === message.id);
        }

        if (matchIdx === -1 && !isFromOther) {
          matchIdx = state.messages.findIndex(
            (m) =>
              (m._status === 'sending' || m._status === 'sent') &&
              String(m.senderUserId) === String(currentUserId),
          );
        }

        if (matchIdx !== -1) {
          state.messages[matchIdx] = {
            ...message,
            _status: 'delivered',
            _tempId: state.messages[matchIdx]._tempId,
          };
        } else if (isFromOther) {
          state.messages.push({ ...message, _status: 'delivered' });
        }
      }
    },

    onConversationRead: (state, action: PayloadAction<Conversation>) => {
      const updatedConv = action.payload;
      if (
        state.activeConversation &&
        state.activeConversation.id === updatedConv.id
      ) {
        state.activeConversation.participants = updatedConv.participants;
      }
    },

    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // List Conversations
      .addCase(eeListConversations.pending, (state) => {
        state.loading = true;
      })
      .addCase(eeListConversations.fulfilled, (state, action) => {
        const { data, totalDocs, nextCursor, isLoadMore } = action.payload;
        if (isLoadMore) {
          state.conversations = [...state.conversations, ...(data || [])];
        } else {
          state.conversations = data || [];
        }
        state.totalDocs = totalDocs ?? state.totalDocs;
        state.nextCursor = nextCursor ?? null;
        state.loading = false;
      })
      .addCase(eeListConversations.rejected, (state) => {
        state.loading = false;
      })

      // Get Single Conversation
      .addCase(eeGetConversation.fulfilled, (state, action) => {
        state.activeConversation = action.payload?.data || action.payload;
      })

      // Create Conversation
      .addCase(eeCreateConversation.fulfilled, (state) => {
        state.totalDocs += 1;
      })

      // Send Message
      .addCase(eeSendMessage.pending, (state) => {
        state.sendingMessage = true;
      })
      .addCase(eeSendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const serverMessage = action.payload?.data || action.payload;
        const uuid = action.payload?._uuid;

        if (uuid) {
          const idx = state.messages.findIndex((m) => m._tempId === uuid);
          if (idx !== -1) {
            const currentStatus = state.messages[idx]._status;
            if (currentStatus === 'delivered') return;

            if (serverMessage?.id && serverMessage?.content) {
              state.messages[idx] = {
                ...serverMessage,
                _status: 'sent',
                _tempId: uuid,
              };
            } else {
              state.messages[idx]._status = 'sent';
              if (serverMessage?.id) {
                state.messages[idx].id = serverMessage.id;
              }
            }
          }
        }
      })
      .addCase(eeSendMessage.rejected, (state) => {
        state.sendingMessage = false;
      })

      // Load Messages
      .addCase(eeLoadMessages.pending, (state) => {
        state.messagesLoading = true;
      })
      .addCase(eeLoadMessages.fulfilled, (state, action) => {
        const { data, nextCursor, isLoadMore } = action.payload;
        const messages = [...(data || [])].reverse();
        if (isLoadMore) {
          state.messages = [...messages, ...state.messages];
        } else {
          state.messages = messages;
        }
        state.messagesNextCursor = nextCursor ?? null;
        state.messagesLoading = false;
      })
      .addCase(eeLoadMessages.rejected, (state) => {
        state.messagesLoading = false;
      })

      // Mark as Read
      .addCase(eeMarkConversationRead.fulfilled, (state, action) => {
        const { id, userId, agencyId } = action.payload;
        const conv = state.conversations.find((c) => c.id === id);
        if (conv) {
          conv.unreadCount = 0;
          const me = conv.participants?.find(
            (p) =>
              (p.userId && String(p.userId) === String(userId)) ||
              (!p.userId &&
                agencyId &&
                String(p.agencyId) === String(agencyId)) ||
              p.isCompanyParentBroadcast,
          );
          if (me) {
            me.lastReadAt = new Date().toISOString();
          }
        }
      });
  },
});

export const employeeMessageReducer = employeeMessageSlice.reducer;
export const employeeMessageActions = {
  ...employeeMessageSlice.actions,
  eeListConversations,
  eeGetConversation,
  eeCreateConversation,
  eeSendMessage,
  eeLoadMessages,
  eeMarkConversationRead,
};
